import React from 'react'
import { isEmpty } from 'lodash/fp'
import { AlertTriangleIcon, PlusIcon, XIcon } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BusinessSettingsView } from '@/widgets/scenario-business'
import { StopListView } from '@/widgets/scenario-stoplist'
import { StrategyView } from '@/widgets/scenario-strategy'

import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import { RHFTextField } from '@/shared/components/HookForm'
import { Alert, AlertTitle } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { SettingsTemplateType } from '@/shared/generated'

import PricingCampaignsSection from './PricingCampaignsSection'
import { useTemplateBuilderContext } from './TemplateBuilderProvider'
import TemplateBuilderSection from './TemplateBuilderSection'

export default function TemplateBuilderCenterPanel() {
	const { t } = useTranslation()

	const { activeType, setActiveType, methods } = useTemplateBuilderContext()

	return (
		<div className='mobile:border mobile:border-solid mobile:p-12 relative flex size-full shrink-0 grow basis-0 flex-col items-center gap-2 self-stretch'>
			<div className='bg-pattern flex size-full w-full flex-col overflow-y-auto'>
				{!isEmpty(methods.formState.errors) && (
					<div className='m-3'>
						<Alert variant='destructive'>
							<AlertTriangleIcon />
							<AlertTitle>
								{t('validation_error.full', {
									defaultValue:
										'Validation failed. Please check the fields and try again.',
								})}
							</AlertTitle>
						</Alert>
					</div>
				)}
				<div className='flex w-full flex-col items-start gap-4 p-5 fade-in'>
					<h3>{t('general_general', { defaultValue: 'General' })}</h3>
					<div className='w-full'>
						<RHFTextField
							name='name'
							label={t('template.name.label', {
								defaultValue: 'Template name',
							})}
							className='w-full'
							autoComplete='off'
							placeholder={t('template.name.placeholder', {
								defaultValue: 'Enter template name',
							})}
						/>
					</div>
					<h3>{t('general_settings')}</h3>
					<div className='grid w-full grid-cols-3 gap-4'>
						<StrategyBlockController
							active={activeType === 'scenario_strategy'}
							onClick={() => setActiveType('scenario_strategy')}
						/>
						<BusinessBlockController
							active={activeType === 'scenario_business_constraints'}
							onClick={() => setActiveType('scenario_business_constraints')}
						/>
						<StopListBlockController
							onClick={() => setActiveType('scenario_stop_list')}
							active={activeType === 'scenario_stop_list'}
						/>
					</div>
					<h3>{t('general_pc')}</h3>
					<div className='grid w-full grid-cols-1 gap-2'>
						<PricingCampaignsBlockController
							active={activeType === 'pricing_campaign'}
							onClick={() => setActiveType('pricing_campaign')}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}

function StrategyBlockController({
	active,
	onClick,
}: {
	active: boolean
	onClick: () => void
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()

	return (
		<Controller
			name='body.settings.strategy'
			control={control}
			render={({ field, fieldState: { error } }) => (
				<div
					onClick={onClick}
					className='relative flex h-full min-h-44 flex-col'
				>
					<TemplateBuilderSection
						id='strategy'
						active={active}
						error={!!error}
						accepts={[SettingsTemplateType.ScenarioStrategy]}
						empty={!field.value}
					>
						{field.value ? (
							<Card className='group/card relative h-full cursor-pointer border fade-in hover:border-primary'>
								<CardHeader>
									<CardTitle>
										{t('pricing_strategy', {
											defaultValue: 'Pricing Strategy',
										})}
									</CardTitle>
									<CardAction>
										<Button
											variant='ghost'
											size='icon-sm'
											onClick={() => field.onChange(null)}
										>
											<XIcon />
										</Button>
									</CardAction>
								</CardHeader>
								<CardContent>
									<ErrorBoundaryWithUser fallback={CardError}>
										<StrategyView data={field.value} />
									</ErrorBoundaryWithUser>
								</CardContent>
							</Card>
						) : (
							<div className='inline-flex items-center gap-1 p-3 text-sm text-muted-foreground'>
								<PlusIcon className='size-4' />{' '}
								{t('settings.strategy.short', { defaultValue: 'Strategy' })}
							</div>
						)}
					</TemplateBuilderSection>
					{!!error?.message && (
						<div className='text-sm text-destructive'>{error?.message}</div>
					)}
				</div>
			)}
		/>
	)
}
function BusinessBlockController({
	active,
	onClick,
}: {
	active: boolean
	onClick: () => void
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()

	return (
		<Controller
			name='body.settings.business'
			control={control}
			render={({ field, fieldState: { error } }) => (
				<div
					onClick={onClick}
					className='relative flex h-full min-h-44 flex-col'
				>
					<TemplateBuilderSection
						id='business'
						active={active}
						error={!!error}
						accepts={[SettingsTemplateType.ScenarioBusinessConstraints]}
						empty={!field.value}
					>
						{field.value ? (
							<Card className='group/card relative col-span-2 h-full cursor-pointer border fade-in hover:border-primary'>
								<CardHeader>
									<CardTitle>
										{t('og.settings.business', {
											defaultValue: 'Business constrains',
										})}
									</CardTitle>
									<CardAction>
										<Button
											variant='ghost'
											size='icon-sm'
											onClick={() => field.onChange(null)}
										>
											<XIcon />
										</Button>
									</CardAction>
								</CardHeader>
								<CardContent>
									<ErrorBoundaryWithUser fallback={CardError}>
										<BusinessSettingsView settings={field.value} compact />
									</ErrorBoundaryWithUser>
								</CardContent>
							</Card>
						) : (
							<div className='inline-flex items-center gap-1 p-3 text-sm text-muted-foreground'>
								<PlusIcon className='size-4' />{' '}
								{t('settings.business.short', { defaultValue: 'Constrains' })}
							</div>
						)}
					</TemplateBuilderSection>
					{!!error?.message && (
						<div className='text-sm text-destructive'>{error?.message}</div>
					)}
				</div>
			)}
		/>
	)
}
function StopListBlockController({
	active,
	onClick,
}: {
	active: boolean
	onClick: () => void
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()

	return (
		<Controller
			name='body.settings.stop_list'
			control={control}
			render={({ field, fieldState: { error } }) => (
				<div
					onClick={onClick}
					className='relative flex h-full min-h-44 flex-col'
				>
					<TemplateBuilderSection
						id='stop_list'
						active={active}
						error={!!error}
						accepts={[SettingsTemplateType.ScenarioStopList]}
						empty={!field.value}
					>
						{field.value ? (
							<Card className='group/card relative col-span-2 h-full cursor-pointer border fade-in hover:border-primary'>
								<CardHeader>
									<CardTitle>
										{t('repricing_stop_list', {
											defaultValue: 'Repricing stop list',
										})}
									</CardTitle>
									<CardAction>
										<Button
											variant='ghost'
											size='icon-sm'
											onClick={() => field.onChange(null)}
										>
											<XIcon />
										</Button>
									</CardAction>
								</CardHeader>
								<CardContent>
									<ErrorBoundaryWithUser fallback={CardError}>
										<StopListView settings={field.value} compact />
									</ErrorBoundaryWithUser>
								</CardContent>
							</Card>
						) : (
							<div className='inline-flex items-center gap-1 p-3 text-sm text-muted-foreground'>
								<PlusIcon className='size-4' />{' '}
								{t('settings.stop_list.short', { defaultValue: 'Stop list' })}
							</div>
						)}
					</TemplateBuilderSection>
					{!!error?.message && (
						<div className='text-sm text-destructive'>{error?.message}</div>
					)}
				</div>
			)}
		/>
	)
}
function PricingCampaignsBlockController({
	active,
	onClick,
}: {
	active: boolean
	onClick: () => void
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()

	return (
		<Controller
			name='body.pricing_campaigns'
			control={control}
			render={({ field, fieldState: { error } }) => (
				<TemplateBuilderSection
					id='pricing_campaigns'
					active={active}
					error={!!error}
					accepts={[SettingsTemplateType.PricingCampaign]}
					empty={!field.value}
				>
					<div className='flex flex-col rounded-lg'>
						<PricingCampaignsSection
							data={field.value || []}
							onChange={field.onChange}
						/>
						<div
							onClick={onClick}
							className='text-mute flex items-center gap-2 rounded-lg p-3 text-sm'
						>
							<PlusIcon className='size-4' />
							{t('add_pricing_campaign', {
								defaultValue: 'Add pricing campaign',
							})}
						</div>
						{!!error?.message && (
							<div className='text-sm text-destructive'>{error?.message}</div>
						)}
					</div>
				</TemplateBuilderSection>
			)}
		/>
	)
}
const CardError = (props: { resetError?: () => void }) => {
	const { t } = useTranslation()

	return (
		<div className='my-2 flex flex-auto items-center justify-center'>
			<Empty className='md:p-0'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<AlertTriangleIcon />
					</EmptyMedia>
					<EmptyTitle>{t('fatal_error_title')}🐞</EmptyTitle>
					<EmptyDescription>{t('fatal_error_desc')}</EmptyDescription>
				</EmptyHeader>
				<EmptyContent>
					{!!props.resetError && (
						<Button
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								props.resetError?.()
							}}
						>
							Retry
						</Button>
					)}
				</EmptyContent>
			</Empty>
		</div>
	)
}
